import "./styles.css";
import { MergeEngineClient } from "./engine/client";
import { ENGINE_PROVENANCE } from "./engine/provenance";
import {
  CATEGORY_LABELS,
  MERGE_VECTORS,
  VECTOR_PROVENANCE,
  draftSectionForVector,
  explainVector,
  findVector,
} from "./fixtures/vectors";
import { partialDeepMatch } from "./merge/expectation";
import { renderMergeResponse } from "./merge/render";
import type {
  MergeInput,
  MergeResponseV1,
  MergeVector,
  VectorCategory,
} from "./merge/types";
import { mountGuidedStory } from "./guided-story/app";
import { resolveDemoRoute } from "./routes";
import { mountStateDemo } from "./state/app";
import { mountTctDemo } from "./tct-demo/app";
import { clearElement, createElement, requireElement } from "./ui/dom";
import { APP_TEMPLATE } from "./ui/template";

const INPUT_LIMIT_BYTES = 1_048_576;
const DEFAULT_VECTOR_ID = "independent-members";
const encoder = new TextEncoder();

function mountMergeDemo(): void {
const app = requireElement<HTMLDivElement>("#app");
app.innerHTML = APP_TEMPLATE;

const scenarioSelect = requireElement<HTMLSelectElement>("#scenario-select");
const scenarioCount = requireElement<HTMLElement>("#scenario-count");
const scenarioCategory = requireElement<HTMLElement>("#scenario-category");
const scenarioTitle = requireElement<HTMLElement>("#scenario-title");
const scenarioSummary = requireElement<HTMLElement>("#scenario-summary");
const scenarioRule = requireElement<HTMLElement>("#scenario-rule");
const scenarioDraftLink = requireElement<HTMLAnchorElement>(
  "#scenario-draft-link",
);
const baseInput = requireElement<HTMLTextAreaElement>("#base-input");
const sourceInput = requireElement<HTMLTextAreaElement>("#source-input");
const targetInput = requireElement<HTMLTextAreaElement>("#target-input");
const baseBytes = requireElement<HTMLElement>("#base-bytes");
const sourceBytes = requireElement<HTMLElement>("#source-bytes");
const targetBytes = requireElement<HTMLElement>("#target-bytes");
const runButton = requireElement<HTMLButtonElement>("#run-button");
const runHint = requireElement<HTMLElement>("#run-hint");
const copyCaseButton = requireElement<HTMLButtonElement>("#copy-case-button");
const resetButton = requireElement<HTMLButtonElement>("#reset-button");
const resultPanel = requireElement<HTMLElement>("#result-panel");
const rawResponse = requireElement<HTMLElement>("#raw-response");
const expectationState = requireElement<HTMLElement>("#expectation-state");
const engineState = requireElement<HTMLElement>(".engine-state");
const engineStateText = requireElement<HTMLElement>("#engine-state-text");
const evidenceVectorCount = requireElement<HTMLElement>(
  "#evidence-vector-count",
);

const engine = new MergeEngineClient();
let running = false;

const defaultVector = findVector(DEFAULT_VECTOR_ID);
if (defaultVector === undefined) {
  throw new Error(`Default vector is missing: ${DEFAULT_VECTOR_ID}`);
}
let selectedVector: MergeVector = defaultVector;

function vectorLabel(vector: MergeVector): string {
  return vector.id
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function populateScenarioPicker(): void {
  clearElement(scenarioSelect);
  const categories = Object.keys(CATEGORY_LABELS) as VectorCategory[];

  for (const category of categories) {
    const group = document.createElement("optgroup");
    group.label = CATEGORY_LABELS[category];
    for (const vector of MERGE_VECTORS.filter(
      (candidate) => candidate.category === category,
    )) {
      const option = document.createElement("option");
      option.value = vector.id;
      option.textContent = vectorLabel(vector);
      group.append(option);
    }
    scenarioSelect.append(group);
  }

  scenarioCount.textContent = `${MERGE_VECTORS.length.toLocaleString()} published Appendix B cases`;
  evidenceVectorCount.textContent = `${VECTOR_PROVENANCE.vectorCount.toLocaleString()} published cases`;
}

function byteLength(value: string): number {
  return encoder.encode(value).byteLength;
}

function updateByteCounter(input: HTMLTextAreaElement, output: HTMLElement): void {
  const bytes = byteLength(input.value);
  output.textContent = `${bytes.toLocaleString()} byte${bytes === 1 ? "" : "s"}`;
  output.dataset.overLimit = String(bytes > INPUT_LIMIT_BYTES);
}

function updateAllByteCounters(): void {
  updateByteCounter(baseInput, baseBytes);
  updateByteCounter(sourceInput, sourceBytes);
  updateByteCounter(targetInput, targetBytes);
}

function currentInput(): MergeInput {
  return {
    base: baseInput.value,
    source: sourceInput.value,
    target: targetInput.value,
  };
}

function isSelectedFixtureUnchanged(): boolean {
  const input = currentInput();
  return (
    input.base === selectedVector.base &&
    input.source === selectedVector.source &&
    input.target === selectedVector.target
  );
}

function updateExpectationBeforeRun(): void {
  if (isSelectedFixtureUnchanged()) {
    expectationState.textContent = "Published fixture loaded";
    expectationState.dataset.state = "fixture";
    return;
  }
  expectationState.textContent = "Custom input · no fixture expectation";
  expectationState.dataset.state = "custom";
}

function updateRunControl(): void {
  if (running) {
    runButton.disabled = true;
    runButton.dataset.running = "true";
    runButton.textContent = "Running…";
    runHint.textContent =
      "Executing locally in the bounded reference-engine worker.";
    return;
  }

  delete runButton.dataset.running;
  if (isSelectedFixtureUnchanged()) {
    runButton.disabled = true;
    runButton.textContent = "Edit inputs to run";
    runHint.textContent =
      "Published scenarios run automatically. Edit any input to run a custom case.";
    return;
  }

  runButton.disabled = false;
  runButton.textContent = "Run edited case";
  runHint.textContent =
    "Ready to run your edited case. Ctrl/Command + Enter works from any input.";
}

function loadVector(vector: MergeVector): void {
  selectedVector = vector;
  scenarioSelect.value = vector.id;

  const explanation = explainVector(vector);
  scenarioCategory.textContent = CATEGORY_LABELS[vector.category];
  scenarioTitle.textContent = explanation.title;
  scenarioSummary.textContent = explanation.summary;
  scenarioRule.textContent = explanation.rule;
  const draftSection = draftSectionForVector(vector);
  scenarioDraftLink.href = draftSection.href;
  scenarioDraftLink.textContent = draftSection.label;

  baseInput.value = vector.base;
  sourceInput.value = vector.source;
  targetInput.value = vector.target;
  updateAllByteCounters();
  updateExpectationBeforeRun();
  updateRunControl();

  clearElement(resultPanel);
  resultPanel.append(
    createElement(
      "p",
      "empty-state",
      "Run the selected case to inspect the complete engine response.",
    ),
  );
  rawResponse.textContent = "Not run yet.";
}

function setEngineState(state: "ready" | "busy" | "failed", text: string): void {
  engineState.dataset.state = state;
  engineStateText.textContent = text;
}

function updateExpectationAfterRun(response: MergeResponseV1): void {
  if (!isSelectedFixtureUnchanged()) {
    expectationState.textContent = "Custom input · no fixture expectation";
    expectationState.dataset.state = "custom";
    return;
  }

  if (partialDeepMatch(response, selectedVector.expected)) {
    expectationState.textContent = "Published expectation matched";
    expectationState.dataset.state = "matched";
    return;
  }

  expectationState.textContent = "Fixture expectation mismatch";
  expectationState.dataset.state = "mismatch";
}

function renderOperationalFailure(error: unknown): void {
  clearElement(resultPanel);
  resultPanel.dataset.status = "operational_failure";
  resultPanel.append(
    createElement("span", "status-badge status-badge--invalid_input", "Demo failure"),
    createElement(
      "p",
      "result-lede",
      error instanceof Error
        ? error.message
        : "The demonstration could not obtain a reference-engine response.",
    ),
  );
  rawResponse.textContent = "No engine response was produced.";
}

async function runMerge(): Promise<void> {
  if (running) {
    return;
  }

  running = true;
  updateRunControl();
  setEngineState("busy", "Initializing and executing the pinned WASM engine");

  try {
    const result = await engine.run(currentInput());
    renderMergeResponse(resultPanel, result.response);
    rawResponse.textContent = result.raw;
    updateExpectationAfterRun(result.response);
    setEngineState(
      "ready",
      `${ENGINE_PROVENANCE.engine} · ${ENGINE_PROVENANCE.version}`,
    );
  } catch (error: unknown) {
    renderOperationalFailure(error);
    expectationState.textContent = "No fixture comparison";
    expectationState.dataset.state = "mismatch";
    setEngineState("failed", "The local reference engine did not complete");
  } finally {
    running = false;
    updateRunControl();
  }
}

async function copyCase(): Promise<void> {
  const payload = JSON.stringify(
    {
      draft: VECTOR_PROVENANCE.sourceDocument,
      vector: isSelectedFixtureUnchanged() ? selectedVector.id : null,
      ...currentInput(),
    },
    null,
    2,
  );

  try {
    await navigator.clipboard.writeText(payload);
    copyCaseButton.textContent = "Copied";
  } catch {
    copyCaseButton.textContent = "Clipboard unavailable";
  }

  window.setTimeout(() => {
    copyCaseButton.textContent = "Copy case";
  }, 1_800);
}

populateScenarioPicker();
loadVector(selectedVector);

scenarioSelect.addEventListener("change", () => {
  const vector = findVector(scenarioSelect.value);
  if (vector !== undefined) {
    loadVector(vector);
    void runMerge();
  }
});

for (const input of [baseInput, sourceInput, targetInput]) {
  input.addEventListener("input", () => {
    updateAllByteCounters();
    updateExpectationBeforeRun();
    updateRunControl();
  });
  input.addEventListener("keydown", (event) => {
    if (
      event.key === "Enter" &&
      (event.ctrlKey || event.metaKey) &&
      !runButton.disabled
    ) {
      event.preventDefault();
      void runMerge();
    }
  });
}

runButton.addEventListener("click", () => {
  void runMerge();
});

copyCaseButton.addEventListener("click", () => {
  void copyCase();
});

resetButton.addEventListener("click", () => {
  loadVector(selectedVector);
  void runMerge();
});

window.addEventListener("beforeunload", () => {
  engine.dispose();
});

void runMerge();
}

const route = resolveDemoRoute(window.location.search);
if (route === "semantic-ast") {
  mountStateDemo();
} else if (route === "tct-delivery") {
  mountTctDemo();
} else if (route === "guided-story") {
  mountGuidedStory();
} else {
  mountMergeDemo();
}
